import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import ReactCalendar from "react-calendar";
import "./DashboardCalendar.css";
import { EventShowModal } from "./component";
import { isSameLocalDay, parseIsoDate } from "../../utils/dashboardAppraisalSummary";

const eventsForDay = (events, day) =>
  events.filter((event) => isSameLocalDay(event.date, day));

const DashboardCalendar = ({ events = [], today }) => {
  const todayDate = parseIsoDate(today) ?? new Date();
  const [value, setValue] = useState(todayDate);
  const [activeStartDate, setActiveStartDate] = useState(todayDate);
  const [showModal, setShowModal] = useState(false);
  const [didSyncToday, setDidSyncToday] = useState(false);

  useEffect(() => {
    if (didSyncToday || !today) return;
    const next = parseIsoDate(today);
    if (!next) return;
    setValue(next);
    setActiveStartDate(next);
    setDidSyncToday(true);
  }, [today, didSyncToday]);

  const dayEvents = eventsForDay(events, value);

  return (
    <section className="dashboard-panel dashboard-calendar" aria-label="PAF calendar">
      <div className="calendar-wrapper">
        <ReactCalendar
          onChange={setValue}
          value={value}
          activeStartDate={activeStartDate}
          onActiveStartDateChange={({ activeStartDate: next }) => {
            if (next) setActiveStartDate(next);
          }}
          locale="en-GB"
          prev2Label={null}
          next2Label={null}
          showNeighboringMonth={false}
          formatShortWeekday={(locale, date) =>
            date.toLocaleDateString(locale, { weekday: "short" }).slice(0, 1)
          }
          tileClassName={({ date, view }) => {
            if (view !== "month") return null;
            const classes = [];
            if (date.getDay() === 6 || date.getDay() === 0) classes.push("weekend");
            if (isSameLocalDay(date, todayDate)) classes.push("dashboard-calendar__today");
            return classes.length ? classes.join(" ") : null;
          }}
          tileContent={({ date, view }) => {
            if (view !== "month") return null;
            const eventsForDate = eventsForDay(events, date);
            return eventsForDate.length > 0 ? (
              <span className="event-dot"></span>
            ) : null;
          }}
        />

        <div className="calendar-event-label">
          {dayEvents.length === 0 ? (
            <span>No event for this date</span>
          ) : dayEvents.length === 1 ? (
            <span>
              <strong>Event Name:</strong> {dayEvents[0].name}
            </span>
          ) : (
            <span>
              <strong>Event Name:</strong> {dayEvents[0].name}{" "}
              <button
                type="button"
                className="calendar-event-label__more"
                onClick={() => setShowModal(true)}
              >
                +{dayEvents.length - 1} more
              </button>
            </span>
          )}
        </div>

        <EventShowModal
          show={showModal}
          onHide={() => setShowModal(false)}
          events={dayEvents}
          date={value}
        />
      </div>
    </section>
  );
};

DashboardCalendar.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      date: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  today: PropTypes.string,
};

export default DashboardCalendar;
